// What makes this build of the shared shell one particular product. Set once
// in the product's main.tsx (configureProduct) before anything renders, and
// read wherever the shell needs to name the product: platform queries, the
// Stripe functions, the brand mark, the locale storage key.
//
// A module-level value rather than React context on purpose: it never changes
// at runtime, and query functions outside the component tree need it too.

import type { ComponentType } from 'react'
import type { Locale } from '@quoter/shared/locale'
import type { CoreStrings } from './i18n/index'

export type ProductConfig = {
  /** products.id in the database and the folder under products/. */
  id: string
  /** Display name — "Mudafolio". */
  name: string
  /** productDictionaries(name, …): platform strings plus the product's own. */
  dictionaries: Record<Locale, CoreStrings>
  /** The brand mark, drawn inline so it scales; shown beside the name. */
  LogoMark: ComponentType<{ size?: number; className?: string }>
  /**
   * Shown until the products row loads (and if it cannot). The row's
   * trial_days is what Checkout actually applies.
   */
  trialDays: number
  /**
   * Where this product's terms and privacy policy live (its landing pages).
   * When set, the sign-in and pricing screens say that continuing means
   * agreeing to them, with links.
   */
  legalUrls?: (locale: Locale) => { terms: string; privacy: string }
}

let current: ProductConfig | null = null

export function configureProduct(config: ProductConfig): void {
  current = config
}

export function getProduct(): ProductConfig {
  if (!current) throw new Error('configureProduct() must be called before the app renders (see main.tsx)')
  return current
}
